import Yup from 'utils/validation/yup-extensions';

/**
 * T_Component FI_ID for the "form" component
 */
export const EPIC_APP_ID = 79;

/**
 * T_Component FI_ID for the "form" component
 */
export const EPIC_COMPONENT_ID = 1;

/**
 * Keys to be used by Formik
 */
export const EPIC_FIELD_KEYS = {
	MODIFICATION_REQUEST: 'modificationRequest',
	COMMENTS: 'comments',
	DEA_NUMBER: 'deaNumber',
	DEA_NUMBER_MIRROR: 'deaNumberMirror',
	DEFAULT_DEPARTMENT: 'defaultDepartment',
	E_PRESCRIBING: 'ePrescribing',
	FUNCTIONAL_ROLE: 'functionalRole',
	FUNCTIONAL_ROLE_CHECKBOX: 'functionalRoleCheckbox',
	SHOW_FUNCTIONAL_ROLE_CHECKBOX: 'showFunctionalRowCheckbox',
	SHOW_FUNCTIONAL_ROLE: 'showFunctionalRole',
	JUSTIFICATION_FOR_FUNCTIONAL_ROLE_OTHER:
		'justificationForFunctionalRoleOther',
	SHOW_JUSTIFICATION_FOR_FUNCTIONAL_ROLE:
		'showJustificationForFunctionalRole',
	SHOW_JUSTIFICATION_FOR_FUNCTIONAL_ROLE_OTHER:
		'showJustificationForFunctionalRoleOther',
	SCHEDULING_ROLE: 'schedulingRole',
	CHANGE_REGISTRATION_BILLING_ACCESS: 'changeRegistrationAndBillingAccess',
	CHANGE_SCHEDULING_ACCESS: 'changeSchedulingAccess',
	SHOW_IMAGING_ROLE: 'showImagingRole',
	LIMITED_ACCESS: 'limitedAccess',
	MODEL_AFTER: 'modelAfter',
	MODEL_AFTER_SHOW: 'modelAfterShow',
	ONE_CONTENT: 'oneContent',
	POSITION: 'position',
	POSITION_LABEL: 'positionLabel',
	SECTION_AMBULATORY: 'sectionAmbulatory',
	SECTION_MAIN: 'sectionMain',
	SECTION_OTHER: 'sectionOther',
	SECTION_REGISTRATION: 'sectionRegistration',
	SECTION_SETTINGS: 'sectionSettings',
	SERVICE_AREAS: 'serviceAreas',
	SHOW_AMBULATORY_FIELDS: 'showAmbulatoryFields',
	SHOW_DEA_NUMBER: 'showDeaNumber',
	SHOW_DEA_NUMBER_MIRROR: 'showDeaNumberMirror',
	SHOW_MODEL_AFTER: 'showModelAfter',
	SHOW_ONE_CONTENT: 'showOneContent',
	SHOW_POSITION_FIELDS: 'showPositionFields',
	SHOW_POSITION_LABEL: 'showPositionLabel',
	SHOW_REGISTRATION_FIELDS: 'showRegistrationFields',
	SHOW_SECTION_MANUAL: 'showSectionManual',
	SHOW_TRAINING_FIELDS_NO: 'showTrainingFieldsNo',
	SHOW_TRAINING_LABEL: 'showTrainingLabel',
	SHOW_VALIDATION_MESSAGE_EPIC_EPIC_CARE: 'showValidationMessageEpicEpicCare',
	TOGGLE_AMBULATORY: 'toggleAmbulatory',
	TOGGLE_REGISTRATION: 'toggleRegistration',
	TRAINING: 'training',
	USER_CREDENTIAL: 'userCredential',
	VALIDATION_MESSAGE_EPIC_EPIC_CARE: 'validationMessageEpicEpicCare',
};

/**
 * Form field metadata (default values, validation schema)
 */
export const EPIC_FIELDS = {
	[EPIC_FIELD_KEYS.COMMENTS]: {
		defaultValue: '',
		validationSchema: Yup.string().max(
			100,
			'You have exceeded the character count (100).'
		),
	},
	[EPIC_FIELD_KEYS.DEA_NUMBER]: {
		defaultValue: '',
		validationSchema: Yup.string().when(
			[
				EPIC_FIELD_KEYS.SHOW_SECTION_MANUAL,
				EPIC_FIELD_KEYS.SHOW_AMBULATORY_FIELDS,
				EPIC_FIELD_KEYS.SHOW_DEA_NUMBER,
			],
			{
				is: true,
				then: Yup.string()
					.required('Please provide a DEA Number.')
					.trim()
					.matches(
						/^[a-zA-Z]{2}\d{7}$/,
						'Invalid DEA Number Format: 2 letters followed by 7 digits (e.g., AB1234567)'
					),
			}
		),
	},
	[EPIC_FIELD_KEYS.DEA_NUMBER_MIRROR]: {
		defaultValue: '',
		validationSchema: Yup.string().when(
			[
				EPIC_FIELD_KEYS.SHOW_MODEL_AFTER,
				EPIC_FIELD_KEYS.SHOW_DEA_NUMBER_MIRROR,
			],
			{
				is: true,
				then: Yup.string()
					.required('Please provide a DEA Number.')
					.trim()
					.matches(
						/^[a-zA-Z]{2}\d{7}$/,
						'Invalid DEA Number Format: 2 letters followed by 7 digits (e.g., AB1234567)'
					),
			}
		),
	},
	[EPIC_FIELD_KEYS.DEFAULT_DEPARTMENT]: {
		defaultValue: '',
		validationSchema: Yup.string().required(
			'Please select a default department.'
		),
	},
	[EPIC_FIELD_KEYS.E_PRESCRIBING]: {
		defaultValue: '',
		validationSchema: Yup.string().when(
			[
				EPIC_FIELD_KEYS.SHOW_SECTION_MANUAL,
				EPIC_FIELD_KEYS.SHOW_AMBULATORY_FIELDS,
				EPIC_FIELD_KEYS.SHOW_POSITION_FIELDS,
			],
			{
				is: true,
				then: Yup.string(),
			}
		),
	},
	[EPIC_FIELD_KEYS.VALIDATION_MESSAGE_EPIC_EPIC_CARE]: {
		defaultValue: '',
		validationSchema: Yup.string().when(
			EPIC_FIELD_KEYS.SHOW_VALIDATION_MESSAGE_EPIC_EPIC_CARE,
			{
				is: true,
				then: Yup.string().required(
					'Please request "Access to Epic Registration, Scheduling, Professional Billing, and Hospital Billing", "Access To EpicCare Ambulatory", or both.'
				),
			}
		),
	},
	[EPIC_FIELD_KEYS.FUNCTIONAL_ROLE]: {
		defaultValue: 'VIEW ONLY',
		validationSchema: Yup.string().when(
			[
				EPIC_FIELD_KEYS.SHOW_REGISTRATION_FIELDS,
				EPIC_FIELD_KEYS.SHOW_SECTION_MANUAL,
				EPIC_FIELD_KEYS.CHANGE_REGISTRATION_BILLING_ACCESS,
				EPIC_FIELD_KEYS.MODIFICATION_REQUEST,
			],
			{
				is: (
					showRegFields,
					showSectionManual,
					changeRegBillingAccess,
					modificationRequest
				) =>
					(showRegFields === true &&
						showSectionManual === true &&
						modificationRequest === false) ||
					changeRegBillingAccess === 'Yes',
				then: Yup.string().required(
					'Please select a Billing/Registration role.'
				),
			}
		),
	},
	[EPIC_FIELD_KEYS.MODIFICATION_REQUEST]: {
		defaultValue: false,
	},
	[EPIC_FIELD_KEYS.CHANGE_REGISTRATION_BILLING_ACCESS]: {
		defaultValue: '',
		validationSchema: Yup.string().when(
			[
				EPIC_FIELD_KEYS.SHOW_REGISTRATION_FIELDS,
				EPIC_FIELD_KEYS.SHOW_SECTION_MANUAL,
				EPIC_FIELD_KEYS.MODIFICATION_REQUEST,
			],
			{
				is: (showRegFields, showSectionManual, modificationRequest) =>
					showRegFields === true &&
					showSectionManual === true &&
					modificationRequest === true,
				then: Yup.string()
					.required('Please select yes or no')
					.oneOf(['Yes', 'No'])
					.test(
						'at-least-one-yes',
						'At least one of Change Registration/Billing Access or Change Scheduling Access must be yes',
						function (value) {
							const CHANGE_SCHEDULING_ACCESS =
								this.parent[
									EPIC_FIELD_KEYS.CHANGE_SCHEDULING_ACCESS
								];
							return (
								value === 'Yes' ||
								CHANGE_SCHEDULING_ACCESS === 'Yes'
							);
						}
					),
				otherwise: Yup.string().oneOf(['Yes', 'No']),
			}
		),
	},
	[EPIC_FIELD_KEYS.CHANGE_SCHEDULING_ACCESS]: {
		defaultValue: '',
		validationSchema: Yup.string().when(
			[
				EPIC_FIELD_KEYS.SHOW_REGISTRATION_FIELDS,
				EPIC_FIELD_KEYS.SHOW_SECTION_MANUAL,
				EPIC_FIELD_KEYS.MODIFICATION_REQUEST,
			],
			{
				is: (showRegFields, showSectionManual, modificationRequest) =>
					showRegFields === true &&
					showSectionManual === true &&
					modificationRequest === true,
				then: Yup.string()
					.required('Please select yes or no')
					.oneOf(['Yes', 'No'])
					.test(
						'at-least-one-yes',
						'At least one of Change Registration/Billing Access or Change Scheduling Access must be yes',
						function (value) {
							const CHANGE_REGISTRATION_BILLING_ACCESS =
								this.parent[
									EPIC_FIELD_KEYS
										.CHANGE_REGISTRATION_BILLING_ACCESS
								];
							return (
								value === 'Yes' ||
								CHANGE_REGISTRATION_BILLING_ACCESS === 'Yes'
							);
						}
					),
				otherwise: Yup.string().oneOf(['Yes', 'No']),
			}
		),
	},
	[EPIC_FIELD_KEYS.JUSTIFICATION_FOR_FUNCTIONAL_ROLE_OTHER]: {
		defaultValue: '',
		validationSchema: Yup.string()
			.when(
				[
					EPIC_FIELD_KEYS.SHOW_SECTION_MANUAL,
					EPIC_FIELD_KEYS.SHOW_REGISTRATION_FIELDS,
					EPIC_FIELD_KEYS.SHOW_JUSTIFICATION_FOR_FUNCTIONAL_ROLE,
					EPIC_FIELD_KEYS.SHOW_JUSTIFICATION_FOR_FUNCTIONAL_ROLE_OTHER,
				],
				{
					is: true,
					then: Yup.string().required(
						'When selecting "other", you need to specify your justification.'
					),
				}
			)
			.when(
				[
					EPIC_FIELD_KEYS.SHOW_MODEL_AFTER,
					EPIC_FIELD_KEYS.SHOW_JUSTIFICATION_FOR_FUNCTIONAL_ROLE,
					EPIC_FIELD_KEYS.SHOW_JUSTIFICATION_FOR_FUNCTIONAL_ROLE_OTHER,
				],
				{
					is: true,
					then: Yup.string().required(
						'When selecting "other", you need to specify your justification.'
					),
				}
			),
	},
	[EPIC_FIELD_KEYS.MODEL_AFTER]: {
		defaultValue: '',
		validationSchema: Yup.string().when(EPIC_FIELD_KEYS.SHOW_MODEL_AFTER, {
			is: true,
			then: Yup.string().required(
				'Please select another user to model roles after.'
			),
		}),
	},
	[EPIC_FIELD_KEYS.MODEL_AFTER_SHOW]: {
		defaultValue: '',
		validationSchema: Yup.string().required(
			'Please indicate if you would like to model roles after another user.'
		),
	},
	[EPIC_FIELD_KEYS.POSITION]: {
		defaultValue: 'Clinical View Only',
		validationSchema: Yup.string().when(
			[
				EPIC_FIELD_KEYS.SHOW_SECTION_MANUAL,
				EPIC_FIELD_KEYS.SHOW_AMBULATORY_FIELDS,
			],
			{
				is: true,
				then: Yup.string().required('Please select a position.'),
			}
		),
	},
	[EPIC_FIELD_KEYS.SERVICE_AREAS]: {
		defaultValue: [],
		validationSchema: Yup.array()
			.of(Yup.string())
			.min(
				1,
				'Please select one or more service area(s).  This is the first two numbers in your Epic Department number. (Example: Gen Med department is 1009120 so SA is 10)'
			),
	},
	[EPIC_FIELD_KEYS.SHOW_AMBULATORY_FIELDS]: {
		defaultValue: false,
		validationSchema: Yup.boolean(),
	},
	[EPIC_FIELD_KEYS.SHOW_DEA_NUMBER]: {
		defaultValue: false,
		validationSchema: Yup.boolean(),
	},
	[EPIC_FIELD_KEYS.SHOW_DEA_NUMBER_MIRROR]: {
		defaultValue: false,
		validationSchema: Yup.boolean(),
	},
	[EPIC_FIELD_KEYS.SHOW_FUNCTIONAL_ROLE_CHECKBOX]: {
		defaultValue: false,
		validationSchema: Yup.boolean(),
	},
	[EPIC_FIELD_KEYS.SHOW_FUNCTIONAL_ROLE]: {
		defaultValue: false,
		validationSchema: Yup.boolean(),
	},
	[EPIC_FIELD_KEYS.FUNCTIONAL_ROLE_CHECKBOX]: {
		defaultValue: false,
		validationSchema: Yup.boolean(),
	},
	[EPIC_FIELD_KEYS.SCHEDULING_ROLE]: {
		defaultValue: 'VIEW ONLY',
		validationSchema: Yup.string().when(
			[
				EPIC_FIELD_KEYS.SHOW_REGISTRATION_FIELDS,
				EPIC_FIELD_KEYS.SHOW_SECTION_MANUAL,
				EPIC_FIELD_KEYS.CHANGE_SCHEDULING_ACCESS,
				EPIC_FIELD_KEYS.MODIFICATION_REQUEST,
			],
			{
				is: (
					showRegFields,
					showSectionManual,
					changeSchedulingAccess,
					modificationRequest
				) =>
					(showRegFields === true &&
						showSectionManual === true &&
						modificationRequest === false) ||
					changeSchedulingAccess === 'Yes',
				then: Yup.string().required('Please select a Scheduling role.'),
			}
		),
	},
	[EPIC_FIELD_KEYS.SHOW_JUSTIFICATION_FOR_FUNCTIONAL_ROLE]: {
		defaultValue: false,
		validationSchema: Yup.boolean(),
	},
	[EPIC_FIELD_KEYS.SHOW_JUSTIFICATION_FOR_FUNCTIONAL_ROLE_OTHER]: {
		defaultValue: false,
		validationSchema: Yup.boolean(),
	},
	[EPIC_FIELD_KEYS.SHOW_VALIDATION_MESSAGE_EPIC_EPIC_CARE]: {
		defaultValue: false,
		validationSchema: Yup.boolean(),
	},
	[EPIC_FIELD_KEYS.SHOW_MODEL_AFTER]: {
		defaultValue: false,
		validationSchema: Yup.boolean(),
	},
	[EPIC_FIELD_KEYS.SHOW_ONE_CONTENT]: {
		defaultValue: false,
		validationSchema: Yup.boolean(),
	},
	[EPIC_FIELD_KEYS.SHOW_REGISTRATION_FIELDS]: {
		defaultValue: false,
		validationSchema: Yup.boolean(),
	},
	[EPIC_FIELD_KEYS.SHOW_SECTION_MANUAL]: {
		defaultValue: false,
		validationSchema: Yup.boolean(),
	},
	[EPIC_FIELD_KEYS.SHOW_TRAINING_FIELDS_NO]: {
		defaultValue: false,
		validationSchema: Yup.boolean(),
	},
	[EPIC_FIELD_KEYS.SHOW_POSITION_FIELDS]: {
		defaultValue: false,
		validationSchema: Yup.boolean(),
	},
	[EPIC_FIELD_KEYS.TRAINING]: {
		defaultValue: '',
		validationSchema: Yup.string().required(
			'Please indicate if training is completed.'
		),
	},
	[EPIC_FIELD_KEYS.TOGGLE_AMBULATORY]: {
		defaultValue: false,
		validationSchema: Yup.boolean().required(
			'Please specify whether you require access to Ambulatory eRecord'
		),
	},

	[EPIC_FIELD_KEYS.TOGGLE_REGISTRATION]: {
		defaultValue: false,
		validationSchema: Yup.boolean().required(
			'Please specify whether you require access to Epic Registration, Scheduling, and Billing'
		),
	},
	[EPIC_FIELD_KEYS.USER_CREDENTIAL]: {
		defaultValue: '',
		validationSchema: Yup.string().when(
			[
				EPIC_FIELD_KEYS.SHOW_SECTION_MANUAL,
				EPIC_FIELD_KEYS.SHOW_AMBULATORY_FIELDS,
			],
			{
				is: true,
				then: Yup.string(),
			}
		),
	},
};

export const EPIC_FIELD_OPTIONS = {
	MODEL_AFTER_NO: 'MANUAL',
	MODEL_AFTER_YES: 'MODEL_AFTER',
	MODEL_AFTER_NO_LABEL: (
		<>
			Manually Select Epic Registration, Scheduling and Billing role
			and/or EpicCare Ambulatory Position{' '}
			<strong>(Highly recommended for practice operations)</strong>
		</>
	),
	MODEL_AFTER_YES_LABEL: (
		<>
			Model security after another user. Modeling will copy the entire
			user account.
		</>
	),
};

export const STATIC_DESCRIPTIONS = {
	FUNCTIONAL_ROLE_S102100024_PROVISIONING:
		'Check this box if this user needs access to Overbook appointments for Imaging Departments.  Please refer to the Role Description document for further information regarding Imaging Roles.',
};
